import { createTheme, keyframes } from '@mui/material/styles';
import { pickerDefaultProps } from './pickerSlots';
import { BRAND, ELEVATION, FONT_FAMILY, NEUTRAL, RADIUS, SEMANTIC, SIDEBAR } from './tokens';

// Entrada do Dialog como bottom sheet no celular (ver MuiDialog.paper abaixo).
const sheetSlideUp = keyframes`
    from { transform: translateY(100%); }
    to { transform: translateY(0); }
`;

/**
 * FlowVendas Design System v2 — MUI theme factory.
 *
 * Ports DESIGN-SYSTEM.md §2 (tokens) and §9 (MUI mapping) into a real
 * `theme.js`. This is the single source of truth for palette/shape/typography
 * and the base-component restyle (§5) — screens should keep consuming
 * `useTheme()` / `sx` as before, they just inherit the new look for free.
 *
 * Sidebar always uses its own dark palette (`theme.customSidebar`), it does
 * NOT derive from `palette.mode` — see DESIGN-SYSTEM.md §2.2 / §9.
 */
export function buildAppTheme(mode = 'light') {
    const isDark = mode === 'dark';
    const neutral = NEUTRAL[isDark ? 'dark' : 'light'];
    const semantic = SEMANTIC[isDark ? 'dark' : 'light'];
    const elevation = ELEVATION[isDark ? 'dark' : 'light'];
    const sidebar = SIDEBAR[isDark ? 'dark' : 'light'];

    const theme = createTheme({
        palette: {
            mode,
            primary: {
                main: BRAND.brand1,
                contrastText: '#ffffff',
            },
            secondary: {
                main: BRAND.purple,
                bg: semantic.purpleBg,
                contrastText: '#ffffff',
            },
            success: {
                main: semantic.success,
                bg: semantic.successBg,
                contrastText: '#ffffff',
            },
            warning: {
                main: semantic.warning,
                bg: semantic.warningBg,
                contrastText: '#ffffff',
            },
            error: {
                main: semantic.danger,
                bg: semantic.dangerBg,
                contrastText: '#ffffff',
            },
            info: {
                main: semantic.info,
                bg: semantic.infoBg,
                contrastText: '#ffffff',
            },
            // Convenience alias so screens/components can reach the brand-2
            // (cyan) accent through the palette instead of importing raw hex.
            brand2: {
                main: BRAND.brand2,
                contrastText: '#ffffff',
            },
            gradient: {
                brand: BRAND.gradientBrand,
            },
            background: {
                default: neutral.bg,
                paper: neutral.surface,
                subtle: neutral.surface2,
            },
            divider: neutral.border,
            text: {
                primary: neutral.text,
                secondary: neutral.text2,
                disabled: neutral.text3,
            },
        },
        shape: {
            borderRadius: RADIUS.card,
        },
        typography: {
            fontFamily: FONT_FAMILY,
            button: {
                textTransform: 'none',
                fontWeight: 600,
            },
        },
        customShadows: elevation,
        customSidebar: sidebar,
        customRadius: RADIUS,
    });

    theme.components = {
        MuiCssBaseline: {
            styleOverrides: {
                body: {
                    fontFamily: FONT_FAMILY,
                },
                // Responsividade: evita scroll horizontal da página inteira
                // quando algum filho estoura a largura em celular/tablet
                // (o scroll horizontal legítimo fica nos containers de tabela).
                'html, body': {
                    maxWidth: '100%',
                    overflowX: 'hidden',
                },
                'img, video': {
                    maxWidth: '100%',
                },
            },
        },
        // Tabelas sempre rolam na horizontal dentro do próprio container em
        // vez de estourar o layout em telas estreitas.
        MuiTableContainer: {
            styleOverrides: {
                root: {
                    overflowX: 'auto',
                    WebkitOverflowScrolling: 'touch',
                },
            },
        },
        // Abas com muitos itens rolam em telas estreitas em vez de cortar.
        MuiTabs: {
            defaultProps: {
                variant: 'scrollable',
                scrollButtons: 'auto',
                allowScrollButtonsMobile: true,
            },
        },
        MuiDialogActions: {
            styleOverrides: {
                root: {
                    flexWrap: 'wrap',
                    gap: 8,
                },
            },
        },
        MuiButton: {
            defaultProps: {
                disableElevation: true,
            },
            styleOverrides: {
                root: {
                    borderRadius: RADIUS.control,
                    fontWeight: 600,
                    fontSize: '13.5px',
                    textTransform: 'none',
                    padding: '10px 16px',
                    // celular: botões de ação (Novo/Importar/Exportar…) mais compactos
                    '@media (max-width:599.95px)': {
                        padding: '7px 12px',
                        fontSize: '12.5px',
                        lineHeight: 1.4,
                        '& .MuiButton-startIcon': { marginRight: '6px', marginLeft: '-2px' },
                        '& .MuiButton-startIcon svg': { width: 14, height: 14 },
                    },
                },
                sizeSmall: {
                    padding: '8px 12px',
                    fontSize: '12.5px',
                    '@media (max-width:599.95px)': { padding: '5px 10px', fontSize: '12px' },
                },
                containedPrimary: {
                    backgroundImage: BRAND.gradientBrand,
                    boxShadow: '0 8px 20px rgba(37,99,235,.35)',
                    '&:hover': {
                        backgroundImage: BRAND.gradientBrand,
                        filter: 'brightness(1.06)',
                        boxShadow: '0 8px 20px rgba(37,99,235,.35)',
                    },
                    '&.Mui-disabled': {
                        backgroundImage: 'none',
                    },
                },
                // Base "ghost" look (§5.1 btn-ghost). Only the *neutral/primary*
                // outlined button (no explicit `color` prop, MUI defaults to
                // "primary") gets the brand-colored border/background — colored
                // variants (`color="error"`, `color="success"`, ...) must keep
                // using their own semantic color, so those live in
                // `outlinedPrimary`/`outlinedError` instead of the generic
                // `outlined` key (which would otherwise win over every color).
                outlined: {
                    backgroundColor: neutral.surface,
                },
                outlinedPrimary: {
                    borderColor: neutral.border,
                    color: neutral.text,
                    '&:hover': {
                        borderColor: BRAND.brand1,
                        color: BRAND.brand1,
                        backgroundColor: neutral.surface,
                    },
                },
                outlinedError: {
                    '&:hover': {
                        backgroundColor: semantic.dangerBg,
                    },
                },
            },
        },
        // Ícone/botão de abrir calendário dos pickers no tamanho do §8 (16px
        // stroke) — ver `pickerSlots.js`.
        MuiDatePicker: { defaultProps: pickerDefaultProps },
        MuiDateTimePicker: { defaultProps: pickerDefaultProps },
        // Sem isso, itens de `Select`/`Autocomplete` caem no default do MUI
        // (1rem/400) em vez do token "Body / table cell" (13px) do
        // DESIGN-SYSTEM.md §2.5 — o campo fechado já usa 13px via `sx` local
        // de cada tela, mas a lista aberta do dropdown ficava maior/fora do
        // padrão por não ter override nenhum. Fix global (mesmo caminho do
        // bug de `MuiInputLabel` abaixo) em vez de repetir `sx` por tela.
        MuiMenuItem: {
            styleOverrides: {
                root: {
                    fontSize: '13px',
                },
            },
        },
        MuiOutlinedInput: {
            styleOverrides: {
                root: {
                    borderRadius: RADIUS.hero - 6, // 12px per §5.5 ff-input
                    backgroundColor: neutral.surface2,
                    '& fieldset': {
                        borderColor: neutral.border,
                    },
                    '&:hover fieldset': {
                        borderColor: BRAND.brand1,
                    },
                    '&.Mui-focused fieldset': {
                        borderColor: BRAND.brand1,
                        borderWidth: 2,
                    },
                },
            },
        },
        // DESIGN-SYSTEM.md §5.5 "Floating label" (`.ff-input`/`.ff-label` in
        // the prototypes): the label only gets its distinctive brand-colored,
        // small-caps treatment in the *shrink* state (focused OR already has
        // a value — `.MuiInputLabel-shrink`, the CSS equivalent of the
        // prototype's `:not(:placeholder-shown)` selector). Gating this only
        // on `.Mui-focused` (the previous rule) misses every filled-but-not-
        // focused field (e.g. a disabled/read-only field with a value, or
        // just clicking away after typing), which is the recurring "label
        // doesn't match the prototype" bug reported across screens — see
        // `pages/products/Form.js` `standaloneFieldSx` for the field-local
        // version of this same fix, now generalized here so every plain
        // `TextField` in the app gets it for free instead of needing a local
        // override per screen.
        //
        // Correção (2026-09-15, 3ª rodada): um commit anterior tentou ir
        // além e portar a caixa inteira do `.ff-input` (altura 52px, padding
        // assimétrico, label vivendo dentro da caixa em vez de cruzar a
        // borda) pra cá, achando que isso "faltava" no fix generalizado
        // abaixo. Só que essa casca de caixa é EXCLUSIVA das telas
        // redesenhadas com o padrão "acordeão + preview" (`products/Form.js`
        // `standaloneFieldSx`, `service_order/Genereate.js` `ffFieldSx`,
        // `customers/Form.js`) — cada uma já aplica isso localmente via `sx`.
        // Generalizar no tema quebrou todo TextField com `label` fora desse
        // padrão (Login, ChangePassword, modais de caixa, Autocomplete/
        // Dropdown, formulários de fornecedores/transações/categorias/
        // orçamento) — a caixa deles continua com a altura/padding padrão do
        // MUI (~56px, notch cruzando a borda), então forçar 52px + o label
        // "de dentro da caixa" só nesse nível global desalinhava tudo.
        // Revertido para manter só a mudança de cor/tipografia do shrink
        // aqui (que é seguro em qualquer caixa) — a casca do `.ff-input`
        // continua sendo responsabilidade do `sx` local de cada formulário
        // redesenhado.
        MuiInputLabel: {
            styleOverrides: {
                root: {
                    '&.Mui-focused': {
                        color: BRAND.brand1,
                    },
                    '&.MuiInputLabel-shrink': {
                        color: BRAND.brand1,
                        fontSize: '10px',
                        fontWeight: 800,
                        letterSpacing: '.04em',
                        textTransform: 'uppercase',
                        transform: 'translate(14px, -9px) scale(1)',
                    },
                },
            },
        },
        MuiChip: {
            styleOverrides: {
                root: {
                    borderRadius: RADIUS.pill,
                    fontWeight: 700,
                    fontSize: '11.5px',
                },
            },
        },
        MuiSwitch: {
            styleOverrides: {
                root: {
                    width: 42,
                    height: 26,
                    padding: 0,
                },
                switchBase: {
                    padding: 2,
                    '&.Mui-checked': {
                        transform: 'translateX(16px)',
                        color: '#fff',
                        '& + .MuiSwitch-track': {
                            backgroundImage: BRAND.gradientBrand,
                            backgroundColor: BRAND.brand1,
                            opacity: 1,
                        },
                    },
                },
                thumb: {
                    width: 22,
                    height: 22,
                    boxShadow: '0 1px 3px rgba(0,0,0,.25)',
                },
                track: {
                    borderRadius: 13,
                    backgroundColor: neutral.border,
                    opacity: 1,
                },
            },
        },
        MuiDialog: {
            styleOverrides: {
                paper: {
                    borderRadius: RADIUS.hero,
                    boxShadow: elevation.lg,
                    // Celular: modal vira bottom sheet (colado no rodapé, largura
                    // total, cantos arredondados só no topo, mesmo raio do
                    // MobileSheet). Seletor com mais especificidade para vencer
                    // `PaperProps.sx` locais (borderRadius/margin/width) e o
                    // GlobalStyles do MobileLayout. fullScreen não é afetado.
                    [theme.breakpoints.down('sm')]: {
                        '&.MuiDialog-paper.MuiPaper-root:not(.MuiDialog-paperFullScreen)': {
                            margin: 0,
                            width: '100%',
                            maxWidth: '100%',
                            borderRadius: '24px 24px 0 0',
                            maxHeight: 'calc(100% - 24px)',
                            '@supports (height: 100dvh)': {
                                maxHeight: 'calc(100dvh - 24px - env(safe-area-inset-top, 0px))',
                            },
                            // Alça (igual ao MobileSheet: 40x4)
                            '&::before': {
                                content: '""',
                                display: 'block',
                                flexShrink: 0,
                                width: 40,
                                height: 4,
                                borderRadius: 2,
                                backgroundColor: neutral.border,
                                margin: '8px auto 2px',
                            },
                            // Respiro inferior da safe-area (iPhone) sem mexer no padding do conteúdo
                            '&::after': {
                                content: '""',
                                display: 'block',
                                flexShrink: 0,
                                height: 'env(safe-area-inset-bottom, 0px)',
                            },
                            // Sobe de baixo (o Fade do Dialog continua no fundo/container)
                            animationName: sheetSlideUp,
                            animationDuration: '280ms',
                            animationTimingFunction: 'cubic-bezier(0.32, 0.72, 0, 1)',
                            animationFillMode: 'both',
                            '@media (prefers-reduced-motion: reduce)': {
                                animationName: 'none',
                            },
                        },
                    },
                    // Tela cheia: acompanha a altura visível (dvh) em vez de
                    // 100% da viewport "grande", que passa do topo/rodapé
                    // quando a barra do navegador mobile está visível.
                    '&.MuiDialog-paperFullScreen': {
                        '@supports (height: 100dvh)': {
                            height: '100dvh',
                            maxHeight: '100dvh',
                        },
                    },
                },
                container: {
                    [theme.breakpoints.down('sm')]: {
                        alignItems: 'flex-end',
                        '@supports (height: 100dvh)': {
                            height: '100dvh',
                        },
                    },
                },
            },
        },
        MuiDialogTitle: {
            styleOverrides: {
                root: {
                    fontSize: '16px',
                    fontWeight: 700,
                },
            },
        },
        MuiDrawer: {
            styleOverrides: {
                paper: {
                    borderRadius: 0,
                    boxShadow: elevation.lg,
                    backgroundColor: neutral.surface,
                    backgroundImage: 'none',
                    // Celular: nunca passar da área visível; conteúdo longo rola dentro do drawer.
                    [theme.breakpoints.down('sm')]: {
                        maxHeight: '100vh',
                        overflowY: 'auto',
                        '@supports (height: 100dvh)': {
                            maxHeight: '100dvh',
                        },
                        '&.MuiDrawer-paperAnchorLeft, &.MuiDrawer-paperAnchorRight': {
                            height: '100vh',
                            '@supports (height: 100dvh)': {
                                height: '100dvh',
                            },
                        },
                    },
                },
            },
        },
        MuiSnackbar: {
            defaultProps: {
                anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
            },
        },
        MuiAlert: {
            styleOverrides: {
                root: {
                    borderRadius: RADIUS.control,
                    fontWeight: 600,
                    fontSize: '13px',
                    alignItems: 'center',
                },
                standardSuccess: { backgroundColor: semantic.successBg, color: semantic.success },
                standardWarning: { backgroundColor: semantic.warningBg, color: semantic.warning },
                standardError: { backgroundColor: semantic.dangerBg, color: semantic.danger },
                standardInfo: { backgroundColor: semantic.infoBg, color: semantic.info },
                filledSuccess: { backgroundColor: semantic.success },
                filledWarning: { backgroundColor: semantic.warning },
                filledError: { backgroundColor: semantic.danger },
                filledInfo: { backgroundColor: semantic.info },
            },
        },
        MuiToggleButtonGroup: {
            styleOverrides: {
                root: {
                    backgroundColor: neutral.surface2,
                    border: `1px solid ${neutral.border}`,
                    borderRadius: RADIUS.control,
                    padding: 2,
                    gap: 2,
                },
            },
        },
        MuiToggleButton: {
            styleOverrides: {
                root: {
                    border: 'none',
                    borderRadius: RADIUS.control - 2,
                    textTransform: 'none',
                    fontWeight: 600,
                    color: neutral.text2,
                    '&.Mui-selected, &.Mui-selected:hover': {
                        backgroundColor: neutral.surface,
                        color: BRAND.brand1,
                        boxShadow: elevation.sm,
                    },
                },
            },
        },
        MuiPaper: {
            styleOverrides: {
                root: {
                    backgroundImage: 'none',
                },
            },
        },
        MuiCard: {
            styleOverrides: {
                root: {
                    borderRadius: RADIUS.card,
                    border: `1px solid ${neutral.border}`,
                    boxShadow: elevation.sm,
                },
            },
        },
        MuiMenu: {
            styleOverrides: {
                paper: {
                    borderRadius: RADIUS.control,
                    boxShadow: elevation.md,
                },
            },
        },
        MuiTooltip: {
            styleOverrides: {
                tooltip: {
                    fontSize: '11.5px',
                    fontWeight: 600,
                },
            },
        },
    };

    return theme;
}

export default buildAppTheme;
